import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { merge, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { IGlobalSettingNodeDto } from '@assess/browser/chimera-api';

export interface IGlobalSettingTreeNode {
  id: string;
  display: string;
  expandable: boolean;
  disabled: boolean;
  level: number;
}

const batchSize = 500;

export class GlobalSettingTree {
  private dispose$ = new Subject<boolean>();
  private validSelections = new Subject<string[]>();

  selections = this.validSelections.asObservable();
  selectAll = false;
  viewableDataSource: MatTreeFlatDataSource<IGlobalSettingNodeDto, IGlobalSettingTreeNode>;

  private readonly treeFlattener: MatTreeFlattener<IGlobalSettingNodeDto, IGlobalSettingTreeNode>;
  private readonly viewableTreeControl: FlatTreeControl<IGlobalSettingTreeNode, IGlobalSettingTreeNode>;
  private readonly fullTreeControl: FlatTreeControl<IGlobalSettingTreeNode, IGlobalSettingTreeNode>;
  private readonly fullDataSource: MatTreeFlatDataSource<IGlobalSettingNodeDto, IGlobalSettingTreeNode>;

  // Keeps track of what nodes are selected and/or indeterminate when filtering
  private checkedSelection = new SelectionModel<string>(true);
  private indeterminateSelection = new SelectionModel<string>(true);

  constructor(
    readonly dataItems: IGlobalSettingNodeDto[],
    selectAll = false
  ) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);

    // Used to control what is shown to user
    this.viewableTreeControl = new FlatTreeControl<IGlobalSettingTreeNode>(this.getLevel, this.isExpandable);
    this.viewableDataSource = new MatTreeFlatDataSource(this.viewableTreeControl, this.treeFlattener);
    this.viewableDataSource.data = dataItems;

    // Used to find descendents and parents including those not currently being shown to user while filtering
    this.fullTreeControl = new FlatTreeControl<IGlobalSettingTreeNode>(this.getLevel, this.isExpandable);
    this.fullDataSource = new MatTreeFlatDataSource(this.fullTreeControl, this.treeFlattener);
    this.fullDataSource.data = dataItems;

    // Anytime a node is selected and the indeterminate are calculated, emit new selection for parent
    merge(this.checkedSelection.changed.asObservable(), this.indeterminateSelection.changed.asObservable())
      .pipe(takeUntil(this.dispose$), debounceTime(100))
      .subscribe((_) => {
        this.checkedSelection.selected.length === this.fullTreeControl.dataNodes.length
          ? (this.selectAll = true)
          : (this.selectAll = false);

        if (this.selectAll) {
          this.validSelections.next(['0']);
        } else {
          this.validSelections.next(this.getValidSelectedIds());
        }
      });

    if (selectAll) {
      this.toggleSelectAll();
    }
  }

  private hasChild = (node: IGlobalSettingTreeNode) => node.expandable;

  private getLevel = (node: IGlobalSettingTreeNode) => node.level;

  private isExpandable = (node: IGlobalSettingTreeNode) => node.expandable;

  private getChildren = (node: IGlobalSettingNodeDto) => node.children;

  private transformer = (node: IGlobalSettingNodeDto, level: number): IGlobalSettingTreeNode => ({
    id: node.id,
    display: node.name,
    expandable: !!node.children && node.children.length > 0,
    disabled: node.disabled,
    level,
  });

  readonly trackById = (_: number, node: IGlobalSettingTreeNode) => node.id;

  get nodes(): IGlobalSettingTreeNode[] {
    return this.viewableTreeControl.dataNodes;
  }

  isExpanded(node: IGlobalSettingTreeNode): boolean {
    return this.viewableTreeControl.isExpanded(node);
  }

  toggleExpansion(node: IGlobalSettingTreeNode): void {
    this.viewableTreeControl.isExpanded(node)
      ? this.viewableTreeControl.collapse(node)
      : this.viewableTreeControl.expand(node);
  }

  isSelected(node: IGlobalSettingTreeNode): boolean {
    return this.checkedSelection.isSelected(node.id);
  }

  setSelectedIds(selectedIds: string[]) {
    if (selectedIds[0] === '0') {
      this.selectAll = false;

      return this.toggleSelectAll();
    }
    const ids = this.fullTreeControl.dataNodes.map((d) => d.id);
    this.deselectCheckedSelection(ids);
    this.deselectIndeterminateSelection(ids);

    selectedIds.forEach((selectedId) => {
      const node = this.fullTreeControl.dataNodes.find((x) => x.id === selectedId);

      if (node) {
        this.toggleSelection(node);
      }
    });
  }

  isIndeterminate(node: IGlobalSettingTreeNode): boolean {
    return this.indeterminateSelection.isSelected(node.id);
  }

  deselectAll(): void {
    const ids = this.fullTreeControl.dataNodes.map((d) => d.id);

    this.deselectCheckedSelection(ids);
    this.deselectIndeterminateSelection(ids);
  }

  deselectCheckedSelection(ids: string[]) {
    for (let i = 0; i < ids.length; i += batchSize) {
      const idsBatch = ids.slice(i, i + batchSize);
      this.checkedSelection.deselect(...idsBatch);
    }
  }

  deselectIndeterminateSelection(ids: string[]) {
    for (let i = 0; i < ids.length; i += batchSize) {
      const idsBatch = ids.slice(i, i + batchSize);
      this.indeterminateSelection.deselect(...idsBatch);
    }
  }

  toggleSelectAll(): void {
    const ids = this.fullTreeControl.dataNodes.map((d) => d.id);
    if (this.selectAll) {
      this.deselectCheckedSelection(ids);
      this.deselectIndeterminateSelection(ids);
    } else {
      // Deselect all and then check each one to make sure everything gets selected
      this.deselectCheckedSelection(ids);
      this.fullTreeControl.dataNodes.filter((d) => d.level === 0).forEach((d) => this.toggleSelection(d));
    }
  }

  filter(filterText: string): void {
    this.viewableDataSource.data = this.filterChildren(filterText, this.dataItems);
    if (filterText === '') {
      this.viewableTreeControl.collapseAll();
    } else {
      this.viewableTreeControl.expandAll();
    }
  }

  dispose() {
    this.validSelections.complete();

    this.dispose$.next(true);
    this.dispose$.complete();
  }

  // attempts to build a subset of selected ids in attempt to keep the CDS search payload as small as possible
  private getValidSelectedIds(): string[] {
    // Filter out nodes that are selected but indeterminate; use a set to improve performance
    const selectedIds = new Set(
      this.checkedSelection.selected.filter((s) => !this.indeterminateSelection.isSelected(s))
    );

    // Get the selected nodes by ensuring that they're checked and then sort by level
    const selectedNodes = this.fullTreeControl.dataNodes
      .filter((node) => selectedIds.has(node.id))
      .sort((a, b) => a.level - b.level);

    // Get any node that isn't disabled - i.e. user has full access to the node and its descendants
    // Using a Set deduplicates and drastically improves lookup performance for deletion later on
    const validNodeIds = new Set(selectedNodes.filter((n) => !n.disabled).map((n) => n.id));

    // get a flattened array of descendants for each node
    const allDescendants = selectedNodes
      .map((node) => (node.expandable ? this.fullTreeControl.getDescendants(node) : []))
      .flatMap((v) => v);

    // deduplicate descendants by converting to a Set
    const uniqueDescendants = new Set(allDescendants);

    for (const d of uniqueDescendants) {
      validNodeIds.delete(d.id);
    }

    return Array.from(validNodeIds.values());
  }

  private filterChildren(filterText: string, nodes: IGlobalSettingNodeDto[]): IGlobalSettingNodeDto[] {
    // Recursively search through dtos while keeping the children array structure
    return nodes.reduce((result: IGlobalSettingNodeDto[], n) => {
      if (n.children.length === 0 && !this.nodeIsVisible(n, filterText)) {
        return result;
      }

      const filteredChildren = this.filterChildren(filterText, n.children);

      if (filteredChildren.length > 0 || this.nodeIsVisible(n, filterText)) {
        result.push({ ...n, children: filteredChildren });
      }

      return result;
    }, []);
  }

  private nodeIsVisible = (node: IGlobalSettingNodeDto, filterText: string): boolean =>
    node.name.trim().toLowerCase().includes(filterText.trim().toLowerCase());

  toggleSelection(node: IGlobalSettingTreeNode): void {
    const fullNode = this.fullTreeControl.dataNodes.find((n) => n.id === node.id);
    if (!fullNode) {
      throw Error('Could not find node.');
    }

    this.selectionModelsToggle(fullNode);

    if (this.hasChild(fullNode)) {
      this.descendantSelectionToggle(fullNode);
    }

    this.updateIndeterminateSelection(fullNode);
    this.updateAllParentsSelection(fullNode);
  }

  private selectionModelsToggle(node: IGlobalSettingTreeNode): void {
    this.checkedSelection.toggle(node.id);
    if (!this.checkedSelection.isSelected(node.id)) {
      this.indeterminateSelection.deselect(node.id);
    }
  }

  private descendantSelectionToggle(node: IGlobalSettingTreeNode): void {
    const descendants = this.fullTreeControl.getDescendants(node).sort((a, b) => b.level - a.level);
    const descendantIds = descendants.map((d) => d.id);
    if (this.checkedSelection.isSelected(node.id)) {
      this.checkedSelection.select(...descendantIds);
      descendants.forEach((d) => {
        this.updateIndeterminateSelection(d);
      });
    } else {
      this.deselectCheckedSelection(descendantIds);
      this.deselectIndeterminateSelection(descendantIds);
    }
  }

  private updateAllParentsSelection(node: IGlobalSettingTreeNode): void {
    let parent: IGlobalSettingTreeNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  private getParentNode(node: IGlobalSettingTreeNode): IGlobalSettingTreeNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.fullTreeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.fullTreeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }

    return null;
  }

  private checkRootNodeSelection(node: IGlobalSettingTreeNode): void {
    const nodeSelected = this.checkedSelection.isSelected(node.id);
    const descendants = this.fullTreeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 && descendants.every((child) => this.checkedSelection.isSelected(child.id));

    if (nodeSelected && !descAllSelected) {
      this.checkedSelection.deselect(node.id);
    } else if (!nodeSelected && descAllSelected) {
      this.checkedSelection.select(node.id);
    }

    this.updateIndeterminateSelection(node);
  }

  private updateIndeterminateSelection(node: IGlobalSettingTreeNode) {
    if (this.determineIfIndeterminate(node)) {
      this.indeterminateSelection.select(node.id);
    } else {
      this.indeterminateSelection.deselect(node.id);
    }
  }

  private determineIfIndeterminate(node: IGlobalSettingTreeNode): boolean {
    if (!this.hasChild(node)) {
      return false;
    }

    const descendants = this.fullTreeControl.getDescendants(node);
    const selectedDescendants = descendants.filter((d) => this.checkedSelection.isSelected(d.id));

    if (selectedDescendants.some((d) => d.disabled)) {
      return true;
    }

    const descSomeIndeterminate = descendants.some((d) => this.indeterminateSelection.isSelected(d.id));
    if (descSomeIndeterminate) {
      return true;
    }

    const descSomeSelected = selectedDescendants.length > 0;

    // If nothing below a disabled node is selected, we don't want to show anything in checkbox
    if (node.disabled) {
      return descSomeSelected;
    }

    return !this.checkedSelection.isSelected(node.id) && descSomeSelected;
  }
}
